'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Konami Code: up, up, down, down, left, right, left, right, b, a
 */
const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
]

const COOKIE_NAME = 'payk12_modern_store'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30 // 30 days

interface UseKonamiCodeOptions {
  onActivate?: (isModern: boolean) => void
  showNotification?: boolean
}

/**
 * Hook that listens for Konami code to toggle modern store mode
 */
export function useKonamiCode(options: UseKonamiCodeOptions = {}) {
  const { onActivate, showNotification = true } = options
  const router = useRouter()
  const inputSequence = useRef<string[]>([])
  const [isModernStore, setIsModernStore] = useState<boolean | null>(null)

  // Read current state from cookie on mount
  useEffect(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${COOKIE_NAME}=`))
      ?.split('=')[1]

    setIsModernStore(cookieValue === 'true')
  }, [])

  const toggleModernStore = useCallback(() => {
    const newValue = !isModernStore
    setIsModernStore(newValue)

    // Set cookie for middleware to read
    document.cookie = `${COOKIE_NAME}=${newValue}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`

    if (showNotification) {
      showToggleNotification(newValue)
    }

    onActivate?.(newValue)

    // Refresh the page to apply the change
    setTimeout(() => {
      router.refresh()
      window.location.reload()
    }, 1500)
  }, [isModernStore, onActivate, router, showNotification])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Ignore events from input elements to prevent sequence pollution
      const target = event.target as HTMLElement
      const tagName = target?.tagName?.toLowerCase()

      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || target?.isContentEditable) {
        return
      }

      // Add the key to the sequence
      inputSequence.current.push(event.code)

      // Keep only the last N keys (length of Konami code)
      if (inputSequence.current.length > KONAMI_CODE.length) {
        inputSequence.current.shift()
      }

      // Check if sequence matches Konami code
      if (inputSequence.current.length === KONAMI_CODE.length) {
        const isMatch = inputSequence.current.every((key, index) => key === KONAMI_CODE[index])

        if (isMatch) {
          inputSequence.current = [] // Reset sequence
          toggleModernStore()
        }
      }
    },
    [toggleModernStore],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  return {
    isModernStore,
    toggleModernStore,
  }
}

/**
 * Roary image for the notification
 */
const ROARY_IMG = `<img src="/img/roary.png" alt="Roary the Raptor" style="width: 150px; height: auto;" />`

/**
 * Show a RAPTASTIC notification when store mode is toggled
 */
function showToggleNotification(isModern: boolean) {
  // Remove any existing notification
  const existing = document.getElementById('konami-notification')
  if (existing) existing.remove()

  // Create notification element
  const notification = document.createElement('div')
  notification.id = 'konami-notification'
  notification.innerHTML = `
    <div class="raptor-overlay">
      <div class="raptor-container">
        <!-- Speech Bubble -->
        <div class="speech-bubble">
          <div class="raptastic-text">RAPTASTIC!</div>
          <div class="mode-text">${isModern ? '🚀 Modern Store Activated!' : '🕹️ Legacy Store Activated!'}</div>
        </div>

        <!-- Roary -->
        <div class="raptor-avatar">
          ${ROARY_IMG}
        </div>

        <!-- Status -->
        <div class="status-text">Reloading page...</div>
      </div>

      <!-- Particle effects -->
      <div class="particles">
        ${Array(12)
          .fill(0)
          .map((_, i) => `<div class="particle particle-${i}"></div>`)
          .join('')}
      </div>
    </div>
  `

  // Add animation styles
  const style = document.createElement('style')
  style.id = 'konami-styles'
  style.textContent = `
    .raptor-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      animation: fadeIn 0.3s ease-out;
    }

    .raptor-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    }

    .speech-bubble {
      background: white;
      border-radius: 20px;
      padding: 20px 40px;
      margin-bottom: -10px;
      position: relative;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
      animation: shake 0.5s ease-in-out 0.3s;
    }

    .speech-bubble::after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 20px solid transparent;
      border-right: 20px solid transparent;
      border-top: 20px solid white;
    }

    .raptastic-text {
      font-family: 'Impact', 'Arial Black', sans-serif;
      font-size: 48px;
      font-weight: 900;
      background: linear-gradient(135deg, #F5A623 0%, #E8941A 50%, #2C3E50 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-transform: uppercase;
      letter-spacing: 2px;
      text-shadow: 2px 2px 0 rgba(44, 62, 80, 0.1);
      animation: textPulse 0.5s ease-in-out infinite alternate;
    }

    .mode-text {
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 16px;
      color: #2C3E50;
      text-align: center;
      margin-top: 8px;
      font-weight: 600;
    }

    .raptor-avatar {
      animation: raptorBounce 0.8s ease-in-out infinite;
      filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
      margin-top: 10px;
    }

    .status-text {
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      color: white;
      margin-top: 20px;
      opacity: 0.8;
      animation: blink 1s ease-in-out infinite;
    }

    .particles {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .particle {
      position: absolute;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #F5A623;
    }

    ${Array(12)
      .fill(0)
      .map(
        (_, i) => `
      .particle-${i} {
        animation: particleFly-${i} 1s ease-out forwards;
        animation-delay: ${i * 0.05}s;
        background: ${i % 2 === 0 ? '#F5A623' : '#2C3E50'};
      }
      @keyframes particleFly-${i} {
        0% { transform: translate(0, 0) scale(1); opacity: 1; }
        100% {
          transform: translate(${Math.cos((i * 30 * Math.PI) / 180) * 150}px, ${
          Math.sin((i * 30 * Math.PI) / 180) * 150
        }px) scale(0);
          opacity: 0;
        }
      }
    `,
      )
      .join('')}

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes bounceIn {
      0% { transform: scale(0) rotate(-10deg); opacity: 0; }
      50% { transform: scale(1.1) rotate(5deg); }
      70% { transform: scale(0.95) rotate(-2deg); }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px) rotate(-2deg); }
      75% { transform: translateX(5px) rotate(2deg); }
    }

    @keyframes textPulse {
      from { transform: scale(1); }
      to { transform: scale(1.05); }
    }

    @keyframes raptorBounce {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      25% { transform: translateY(-10px) rotate(-3deg); }
      75% { transform: translateY(-5px) rotate(3deg); }
    }

    @keyframes blink {
      0%, 100% { opacity: 0.8; }
      50% { opacity: 0.4; }
    }
  `

  // Remove existing styles if any
  const existingStyle = document.getElementById('konami-styles')
  if (existingStyle) existingStyle.remove()

  document.head.appendChild(style)
  document.body.appendChild(notification)

  // Play a raptor roar sound effect
  playRaptorRoar(isModern)
}

/**
 * Play a fun raptor roar sound using Web Audio API
 */
function playRaptorRoar(isModern: boolean) {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

    // Create a more dramatic sound
    const createTone = (freq: number, startTime: number, duration: number, type: OscillatorType = 'sawtooth') => {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      const filter = audioContext.createBiquadFilter()

      oscillator.connect(filter)
      filter.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + startTime)
      oscillator.frequency.exponentialRampToValueAtTime(freq * 0.5, audioContext.currentTime + startTime + duration)
      oscillator.type = type

      filter.type = 'lowpass'
      filter.frequency.value = 2000

      gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime)
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + startTime + 0.05)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration)

      oscillator.start(audioContext.currentTime + startTime)
      oscillator.stop(audioContext.currentTime + startTime + duration)
    }

    // Victory fanfare notes
    const notes = isModern
      ? [392, 523.25, 659.25, 783.99, 1046.5] // Ascending (modern = upgrade!)
      : [783.99, 659.25, 523.25, 392, 329.63] // Descending (legacy = going back)

    notes.forEach((freq, i) => {
      createTone(freq, i * 0.12, 0.3, 'square')
    })

    // Add a bass drop for extra drama
    createTone(110, 0.5, 0.4, 'sawtooth')
  } catch {
    // Audio not supported, no problem
  }
}

export default useKonamiCode
